import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { fieldTypeRaw, ModularSurveyResponse, StructureField } from '../../interfaces';
import { DragHandleComponent } from "src/app/shared/drag-handle/drag-handle.component";
import { SingleResponseFieldWrapperComponent, TextFieldWrapperComponent, MultipleResponseFieldWrapperComponent, LevelSatisfactionFieldWrapperComponent } from "./components";
import { ModularSurvey, ModularSurveyForm, ModularSurveyFormFactory } from '../../services';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Snackbar } from 'src/app/services';
import { TranslationService } from 'src/app/services/translation.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';

@Component({
  selector: 'app-modular-survey',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    DragHandleComponent,
    SingleResponseFieldWrapperComponent,
    TextFieldWrapperComponent,
    MultipleResponseFieldWrapperComponent,
    LevelSatisfactionFieldWrapperComponent
],
  templateUrl: './modular-survey.component.html',
  styleUrls: ['./modular-survey.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModularSurveyComponent {
  // Data source injections (all optional)
  private _dialogData = inject<ModularSurveyResponse>(MAT_DIALOG_DATA, { optional: true });
  private _dialogRef = inject<MatDialogRef<ModularSurveyComponent>>(MatDialogRef, { optional: true });
  private _bottomSheetData = inject<ModularSurveyResponse>(MAT_BOTTOM_SHEET_DATA, { optional: true });

  // Service injections
  private _modularSurveyService = inject(ModularSurvey);
  private _modularSurveyForm = inject(ModularSurveyForm);
  private _modularSurveyFormFactory = inject(ModularSurveyFormFactory);
  private _translationService = inject(TranslationService);
  protected readonly lang = this._translationService.selectedLanguage;
  private _snackbarService = inject(Snackbar);
  private _destroyRef = inject(DestroyRef);

  // Input signal (for direct usage in templates)
  public surveyDataInput = input<ModularSurveyResponse | null>(null);

  // Resolved survey data: dialog > bottom sheet > input > service
  protected surveyData = computed<ModularSurveyResponse | null>(() => {
    return this._dialogData
      ?? this._bottomSheetData
      ?? this.surveyDataInput()
      ?? this._modularSurveyService.surveyData();
  });

  // Rendering mode
  protected isBottomSheetMode = computed(() => !this._dialogRef && !this._dialogData);
  protected isDialogMode = computed(() => !!this._dialogRef || !!this._dialogData);

  // Local signals
  protected fieldList = signal<StructureField[]>([]);

  protected typeText = fieldTypeRaw.text;
  protected typeTextarea = fieldTypeRaw.textarea;
  protected typeSelectionUnique = fieldTypeRaw.selection_unique;
  protected typeSelectionMulti = fieldTypeRaw.selection_multi;
  protected typeSatisfactionLevel3 = fieldTypeRaw.satisfaction_level_3;
  protected typeSatisfactionLevel5 = fieldTypeRaw.satisfaction_level_5;
  protected typeEmail = fieldTypeRaw.email;

  // Form
  public surveyForm = signal<FormGroup | null>(null);

  // Effects
  setFieldListAndFormGroup = effect(() => {
    if (this.surveyData()) {
      this.fieldList.set(this.surveyData().structure[this.lang()] || []);
    }

    if(!this.fieldList() || this.fieldList().length === 0 ) return;

    this.surveyForm.set(this._modularSurveyFormFactory.createForm(this.fieldList()));
  }, { allowSignalWrites: true });

  // Methods
  protected isLastField(index: number): boolean {
    return index === this.fieldList().length - 1;
  }

  protected close(): void {
    if (this._dialogRef) {
      this._dialogRef.close();
    } else {
      this._modularSurveyForm.toggleBottomSheet();
    }
  }

  protected onSubmit() {
    this._modularSurveyForm.setMarkAllFormFieldsAsTouched(true);

    if (!this.surveyForm()) return;

    if (!this.surveyForm()?.valid) {
      const errorMessage = this._translationService.instant('No se ha enviado la encuesta. Por favor, revise los campos');
      this._snackbarService.showErrorSnackbar(errorMessage);
      return;
    }

    const surveyId = this.surveyData()?.id;
    if (!surveyId) return;

    this._modularSurveyForm.submitForm(this.surveyForm(), surveyId, this.fieldList())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          const successMessage = this._translationService.instant('Encuesta enviada con éxito');
          this._snackbarService.showSuccessSnackbar(successMessage);
          this._modularSurveyFormFactory.resetForm(this.surveyForm());

          if (this._dialogRef) {
            // Close the dialog and return the result 'submitted'
            this._dialogRef.close('submitted');
          } else {
            this._modularSurveyForm.toggleBottomSheet();
          }
        },
        error: () => {
          const errorMessage = this._translationService.instant('Error al enviar la encuesta');
          this._snackbarService.showErrorSnackbar(errorMessage);
        },
      });
  }
}
